import { Controller } from "@hotwired/stimulus"

// Keeps the running calorie total in step with hand-edited macros.
//
// A custom split that quietly misses the target by 300 kcal would undo the
// whole calculation, so the sum is on screen as you type rather than checked
// when you submit.
//
// The arithmetic is duplicated from Thrive::Calculators::Macros, which is
// worth being uneasy about — but these constants are physical facts (a gram
// of protein is 4 kcal), not policy, and a round trip per keystroke to
// display a number is not worth it.
const KCAL_PER_GRAM = { protein: 4, carbs: 4, fat: 9 }

// Anything inside this of the target is rounding, not a mistake. Matches
// Macros::TOLERANCE_PERCENTAGE.
const TOLERANCE = 0.03

export default class extends Controller {
  static targets = ["protein", "carbs", "fat", "total", "customised"]
  static values = { target: Number }

  connect() {
    // Render the total without marking the split as hand-set — arriving on
    // the step is not the same as editing it.
    this.render()
  }

  // Typing in a macro field means this split is hand-set, so a later
  // recalculation must not overwrite numbers somebody deliberately chose.
  recalculate() {
    if (this.hasCustomisedTarget) this.customisedTarget.value = "1"
    this.render()
  }

  render() {
    const total =
      this.gramsFor("protein") * KCAL_PER_GRAM.protein +
      this.gramsFor("carbs") * KCAL_PER_GRAM.carbs +
      this.gramsFor("fat") * KCAL_PER_GRAM.fat

    this.totalTarget.textContent = total.toLocaleString()

    // Colour rather than an error: a split slightly off target is a choice,
    // not a mistake to block on.
    const drift = Math.abs(total - this.targetValue)
    const within = this.targetValue > 0 && drift / this.targetValue <= TOLERANCE
    this.totalTarget.style.color = within
      ? "var(--color-positive)"
      : "var(--color-protein)"
  }

  gramsFor(name) {
    const field = this[`${name}Target`]
    const value = parseFloat(field?.value)
    return isNaN(value) ? 0 : value
  }
}
